import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://sugargoosheet.autos';

interface ArticleLayoutProps {
  title: string;
  description: string;
  imageUrl: string;
  readTime: string;
  dateUpdated: string;
  breadcrumbs: { label: string; href?: string }[];
  children: React.ReactNode;
}

function buildArticleSchema(props: ArticleLayoutProps, pageUrl: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: props.title,
    description: props.description,
    image: props.imageUrl,
    datePublished: '2026-08-01',
    dateModified: '2026-08-09',
    author: {
      '@type': 'Organization',
      name: 'Sugargoo Spreadsheet Resource Hub',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Sugargoo Spreadsheet Resource Hub',
      url: siteUrl,
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/1fc56da0b352904925ac780066826afe.jpeg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': pageUrl,
    },
    inLanguage: 'en',
    timeRequired: `PT${props.readTime}M`,
  };
}

export default function ArticleLayout({ title, description, imageUrl, readTime, dateUpdated, breadcrumbs, children }: ArticleLayoutProps) {
  const location = useLocation();
  const pageUrl = `${siteUrl}${location.pathname}`;
  const articleSchema = buildArticleSchema({ title, description, imageUrl, readTime, dateUpdated, breadcrumbs }, pageUrl);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      <Navbar />

      <main>
        <section className="pt-24 pb-12 md:pt-32 md:pb-16 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-500 mb-4">
              {breadcrumbs.map((crumb, i) => (
                <span key={i} className="flex items-center gap-1.5">
                  {i > 0 && <span className="text-foreground-300">/</span>}
                  {crumb.href ? (
                    <Link to={crumb.href} className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">{crumb.label}</Link>
                  ) : (
                    <span className="text-foreground-700 font-medium whitespace-nowrap">{crumb.label}</span>
                  )}
                </span>
              ))}
            </div>

            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent-100/80 text-accent-800 text-xs font-medium mb-6">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-article-line"></i>
              </div>
              <span className="whitespace-nowrap">Updated {dateUpdated} &bull; {readTime} min read</span>
            </div>

            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950 leading-tight mb-4">
              {title}
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
              {description}
            </p>

            <div className="w-full h-56 md:h-72 rounded-xl overflow-hidden">
              <img
                src={imageUrl}
                alt={title}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        <article className="py-10 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="space-y-10">
              {children}
            </div>

            <div className="bg-background-100 border border-background-200/60 rounded-xl p-8 text-center mt-12">
              <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-3">Ready to Start Shopping Smarter?</h3>
              <p className="text-sm text-foreground-600 mb-6">Browse our organized product categories and explore more Sugargoo Spreadsheet resources.</p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/"
                  className="px-6 py-2.5 text-sm font-semibold text-background-50 bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Back to Homepage
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-2.5 text-sm font-semibold text-foreground-900 bg-background-50 border-2 border-foreground-200 rounded-lg hover:border-foreground-400 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Visit Sugargoo Spreadsheet Shop
                </a>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}